import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
  isClassificationUnavailable,
  isRequirementUnavailable,
} from '../utils/jupasClassificationDisplay';
import DSEScoreInputer from '../components/widgets/DSEScoreInputer';
import ProgrammeSearch from '../components/widgets/ProgrammeSearch';
import ProgrammeRecommendations from '../components/widgets/ProgrammeRecommendations';
import programmeService from '../services/programmeService';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { isDseScoreSetComplete } from '../utils/dseScoreCompleteness';
import { calculateCompetitivenessFromDseScores } from '../utils/dseScoreCalculations';
import { getJupasOrderBandColor, getJupasOrderRankLabel } from '../utils/jupasOrderDisplay';
import { JUPAS_SIX_ROW_LIST_MIN_HEIGHT_CLASS } from '../utils/jupasLayoutDisplay';
import JupasProgrammeCard from '../components/widgets/JupasProgrammeCard';
import JupasProgrammeSplitLayout from '../components/widgets/JupasProgrammeSplitLayout';
import ProgrammeDetailsPanel from '../components/widgets/ProgrammeDetailsPanel';
import JupasCalculatorWorkspace from '../components/widgets/JupasCalculatorWorkspace';
import JupasScoreSidebar from '../components/widgets/JupasScoreSidebar';
import JupasSelectorSidebar from '../components/widgets/JupasSelectorSidebar';
import JupasRankingSidebar from '../components/widgets/JupasRankingSidebar';
import PageTutorialGuide from '../components/tutorial/PageTutorialGuide';
import TutorialInlineButton from '../components/tutorial/TutorialInlineButton';
import BugReportHelperButton from '../components/widgets/BugReportHelperButton';
import ContactBox from '../components/widgets/ContactBox';
import LoadingSpinner from '../components/blocks/LoadingSpinner';
import StorageStatusIndicator from '../components/widgets/StorageStatusIndicator';
import GuestJupasStorageModal from '../components/widgets/GuestJupasStorageModal';
import { resolveTutorialGuideSteps } from '../components/tutorial/tutorialGuideSteps';
import { usePageTutorial } from '../components/tutorial/usePageTutorial';
import {
  cacheJupasGuestState,
  clearJupasGuestState,
  JUPAS_GUEST_NOTICE_SEEN_KEY,
  readJupasGuestState,
} from '../utils/jupasGuestResume';

const SPLIT_PIPELINE_MIN_YEAR = 2026;
const FIXED_JUPAS_YEAR = 2026;
const FIXED_JUPAS_COLLECTION = `programmes_${FIXED_JUPAS_YEAR}`;
const REQUIREMENT_BATCH_SIZE = 40;
const CLASSIFICATION_PRIORITY_SIZE = 10;
const CLASSIFICATION_BACKGROUND_BATCH_SIZE = 30;
const CLASSIFICATION_AUTO_RETRY_ROUNDS = 3;

const TUTORIAL_PROGRAMMES = [
  { code: 'DEMO01', institution: 'HKU', full_title_en: 'Business Management (Example)', full_title_cn: '商業管理（示例）' },
  { code: 'DEMO02', institution: 'CUHK', full_title_en: 'Data Science (Example)', full_title_cn: '數據科學（示例）' },
  { code: 'DEMO03', institution: 'PolyU', full_title_en: 'Design (Example)', full_title_cn: '設計（示例）' },
];

const TUTORIAL_CLASSIFICATIONS = {
  DEMO01: { classification: 7, requirement: 'P' },
  DEMO02: { classification: 5, requirement: 'C' },
  DEMO03: { classification: 2, requirement: 'W' },
};

const TUTORIAL_ANCHOR_TO_STEP = {
  dse: 'scores',
  search: 'programmes',
  ordering: 'ranking',
};

const toScoresObject = (rows) => {
  const out = {};
  rows.forEach((row) => {
    if (row.subjectCode && row.score) {
      out[row.subjectCode] = row.score;
    }
  });
  return out;
};

const getCollectionYear = (collection) => {
  const match = String(collection || '').match(/\d{4}$/);
  return match ? Number(match[0]) : null;
};

const compareProgrammeCodes = (a, b) =>
  String(a?.code ?? '').localeCompare(String(b?.code ?? ''));

const usesSplitJupasPipeline = (collection) => {
  const year = getCollectionYear(collection);
  return year != null && year >= SPLIT_PIPELINE_MIN_YEAR;
};

const supportsRequirementFulfillment = (collection) => {
  const year = getCollectionYear(collection);
  return year != null && year >= SPLIT_PIPELINE_MIN_YEAR;
};

const getLatestOfferTotal = (programme) => Number(programme?.stats_offer?.[0]?.[6] || 0);

const shouldCheckJupasBandRequirement = (activeStep, ranking) => (
  activeStep === 'ranking'
  && Number.isInteger(ranking)
  && ranking >= 1
  && ranking <= 20
);

const institutionMatches = (programme, institutions) => {
  if (!institutions?.length) return true;
  const programmeInstitution = String(programme?.institution || '').toLowerCase();
  return institutions.some((institution) => {
    const filter = String(institution || '').toLowerCase();
    return filter && programmeInstitution.includes(filter);
  });
};

const toSavedOrder = (programmes) => {
  const savedOrder = {};
  programmes.forEach((programme, index) => {
    savedOrder[programme.code] = index;
  });
  return savedOrder;
};

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

async function fetchJsonWithRetry(url, options, retries = 1) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await fetch(url, options);
      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (error) {
        data = { message: text };
      }

      // Do not retry 429: repeating a large batch would amplify the rate limit.
      // Let the UI surface one explicit rate-limit error instead.
      if (response.status >= 500 && attempt < retries) {
        await wait(300 * (attempt + 1));
        continue;
      }
      return { response, data };
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await wait(300 * (attempt + 1));
        continue;
      }
    }
  }
  throw lastError || new Error('Network request failed');
}

async function mapWithConcurrency(items, limit, mapper) {
  const results = new Array(items.length);
  let nextIndex = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index], index);
    }
  });
  await Promise.all(workers);
  return results;
}

const JUPASCalculator = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const guestResumeState = useMemo(() => readJupasGuestState(), []);
  const forceTutorial = searchParams.get('tutorial') === '1';
  const seedScoresFromPromo = useMemo(() => {
    const incoming = location.state?.dseScores;
    const source = Array.isArray(incoming) && incoming.length > 0
      ? incoming
      : guestResumeState?.scores;
    if (!Array.isArray(source) || source.length === 0) return null;
    return source
      .filter((row) => row?.subjectCode && row?.score)
      .map(({ subjectCode, score }) => ({ subjectCode, score }));
  }, [guestResumeState, location.state]);
  const { guideOpen, openGuide, closeGuide } = usePageTutorial('jupas', { forceOpen: forceTutorial });
  const [tutorialAnchor, setTutorialAnchor] = useState(null);
  const dseTutorialRef = useRef(null);
  const searchTutorialRef = useRef(null);
  const orderingTutorialRef = useRef(null);
  const tutorialAnchorRefs = {
    dse: dseTutorialRef,
    search: searchTutorialRef,
    ordering: orderingTutorialRef,
  };
  const jupasGuideSteps = resolveTutorialGuideSteps('jupas', t);
  const { isAuthenticated, refreshUserProfile } = useAuth();
  const [activeStep, setActiveStep] = useState(() => (
    ['scores', 'programmes', 'ranking'].includes(guestResumeState?.activeStep)
      ? guestResumeState.activeStep
      : 'scores'
  ));
  const [guestStoragePrompt, setGuestStoragePrompt] = useState(null);
  const guestResumeAppliedRef = useRef(false);
  const saveProgrammeOrderRef = useRef(null);
  const guestResumePersistenceRef = useRef({
    scores: !guestResumeState?.scores?.length,
    programmes: !guestResumeState?.selectedProgrammes?.length,
  });
  const markGuestResumePersisted = useCallback((kind) => {
    guestResumePersistenceRef.current[kind] = true;
    if (guestResumePersistenceRef.current.scores && guestResumePersistenceRef.current.programmes) {
      clearJupasGuestState();
    }
  }, []);
  const [scores, setScores] = useState([]);
  const [scoreSaveState, setScoreSaveState] = useState({ isLoading: false, saveStatus: '' });
  // Calculations use the last score profile committed by leaving the Scores step.
  // Editing an individual score must not generate backend work.
  const [committedScores, setCommittedScores] = useState([]);
  const selectedCollection = FIXED_JUPAS_COLLECTION;
  const [allProgrammes, setAllProgrammes] = useState([]);
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [selectedRequirementFilters, setSelectedRequirementFilters] = useState([]);
  const [selectedClassificationFilters, setSelectedClassificationFilters] = useState([]);
  const [selectedProgramme, setSelectedProgramme] = useState(null);
  const [selectedProgrammes, setSelectedProgrammes] = useState([]);
  const [recommendedProgrammes, setRecommendedProgrammes] = useState([]);
  const [programmeClassificationMap, setProgrammeClassificationMap] = useState({});
  const [selectedProgrammeEvaluation, setSelectedProgrammeEvaluation] = useState(null);
  const [selectedProgrammeEvaluationLoading, setSelectedProgrammeEvaluationLoading] = useState(false);
  const classificationCacheRef = useRef({ key: '', values: {}, pendingCodes: new Set() });
  const programmeDetailCacheRef = useRef(new Map());
  const classificationRetryRef = useRef(new Map());
  const programmeEvaluationRequestIdRef = useRef(0);
  const unavailableRefreshSessionRef = useRef(null);
  const requirementRequestIdRef = useRef(0);
  const previousActiveStepRef = useRef('scores');
  const [requirementRefreshRequest, setRequirementRefreshRequest] = useState(null);
  const [classificationRefreshNonce, setClassificationRefreshNonce] = useState(0);
  const [classificationLoadingCodes, setClassificationLoadingCodes] = useState(() => new Set());
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [recommendationsError, setRecommendationsError] = useState('');

  useEffect(() => {
    if (guideOpen) setActiveStep('scores');
  }, [guideOpen]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [draggedCardHeight, setDraggedCardHeight] = useState(0);
  const dragPreviewRef = useRef(null);
  const dragSessionRef = useRef(null);
  const orderListRef = useRef(null);
  const [orderSaveStatus, setOrderSaveStatus] = useState('');
  const [orderSaving, setOrderSaving] = useState(false);
  const [programmeSearchState, setProgrammeSearchState] = useState({
    query: '',
    results: [],
    loading: false,
    error: null,
  });
  const selectedYear = useMemo(() => getCollectionYear(selectedCollection), [selectedCollection]);
  const scoresComplete = useMemo(() => isDseScoreSetComplete(scores), [scores]);
  const scoreSummary = useMemo(
    () => calculateCompetitivenessFromDseScores(scores.filter((row) => row.subjectCode && row.score)),
    [scores]
  );
  const programmesComplete = selectedProgrammes.length >= 1;
  const isProgrammeSearchActive = programmeSearchState.query.trim().length > 0;
  const filteredProgrammeCandidates = useMemo(
    () => allProgrammes.filter((programme) => institutionMatches(programme, selectedInstitutions)),
    [allProgrammes, selectedInstitutions]
  );
  const advancedFiltersCalculating = useMemo(() => {
    if (selectedRequirementFilters.length === 0 && selectedClassificationFilters.length === 0) {
      return false;
    }

    const candidates = filteredProgrammeCandidates;
    const hasPendingClassification = candidates.some((programme) => (
      programme?.code
      && (
        classificationLoadingCodes.has(programme.code)
        || !Object.prototype.hasOwnProperty.call(programmeClassificationMap, programme.code)
      )
    ));

    return hasPendingClassification;
  }, [
    classificationLoadingCodes,
    filteredProgrammeCandidates,
    programmeClassificationMap,
    selectedClassificationFilters,
    selectedRequirementFilters,
  ]);

  const programmesListSpinnerVisible = useMemo(() => {
    if (isProgrammeSearchActive) return programmeSearchState.loading;
    if (recommendationsLoading || advancedFiltersCalculating) return true;
    return false;
  }, [
    advancedFiltersCalculating,
    isProgrammeSearchActive,
    programmeSearchState.loading,
    recommendationsLoading,
  ]);

  const rankedProgrammeClassificationMap = useMemo(() => {
    const ranked = {};
    Object.entries(programmeClassificationMap).forEach(([code, entry]) => {
      ranked[code] = {
        ...entry,
        requirement: entry?.rankedRequirement ?? entry?.requirement,
      };
    });
    return ranked;
  }, [programmeClassificationMap]);

  const programmeMatchesAdvancedFilters = useCallback((programme) => {
    const entry = programmeClassificationMap[programme.code];
    const rawClassification = entry?.classification ?? programme.classification;
    const classification = Number(rawClassification);
    if (selectedClassificationFilters.length > 0
      && (!Number.isFinite(classification) || !selectedClassificationFilters.includes(classification))) {
      return false;
    }

    if (selectedRequirementFilters.length > 0) {
      const requirementCode = String(entry?.requirement || '').toUpperCase();
      if (!requirementCode || !selectedRequirementFilters.includes(requirementCode)) return false;
    }

    return true;
  }, [programmeClassificationMap, selectedClassificationFilters, selectedRequirementFilters]);

  const handleStepChange = useCallback((step) => {
    if (step === 'programmes' && !isDseScoreSetComplete(scores)) return;
    if (step === 'ranking' && selectedProgrammes.length === 0) return;

    const completeStepChange = () => {
      if (activeStep === 'scores' && step !== 'scores') {
        setCommittedScores(scores);
      }
      setActiveStep(step);
    };

    const leavingScoresAsGuest = activeStep === 'scores'
      && step !== 'scores'
      && !isAuthenticated;
    const noticeAlreadySeen = typeof window !== 'undefined'
      && localStorage.getItem(JUPAS_GUEST_NOTICE_SEEN_KEY) === '1';

    if (leavingScoresAsGuest && !noticeAlreadySeen) {
      setGuestStoragePrompt({
        reason: 'leave-scores',
        onProceed: () => {
          localStorage.setItem(JUPAS_GUEST_NOTICE_SEEN_KEY, '1');
          completeStepChange();
        },
      });
      return;
    }

    completeStepChange();
  }, [activeStep, scores, selectedProgrammes, isAuthenticated]);

  const refreshSelectedProgrammeEvaluations = useCallback(() => {
    if (!selectedCollection
      || !isDseScoreSetComplete(committedScores)
      || selectedProgrammes.length === 0) {
      return;
    }

    if (supportsRequirementFulfillment(selectedCollection)) {
      setRequirementRefreshRequest({
        id: Date.now(),
        programmes: selectedProgrammes,
        orderedProgrammes: selectedProgrammes,
      });
    }
  }, [committedScores, selectedCollection, selectedProgrammes]);

  useEffect(() => {
    const enteredRanking = activeStep === 'ranking'
      && previousActiveStepRef.current !== 'ranking';
    previousActiveStepRef.current = activeStep;
    if (!enteredRanking) return;
    refreshSelectedProgrammeEvaluations();
  }, [activeStep, refreshSelectedProgrammeEvaluations]);

  const handleTutorialAnchorChange = useCallback((anchor) => {
    setTutorialAnchor(anchor);
    const step = TUTORIAL_ANCHOR_TO_STEP[anchor];
    if (!step) return;
    // Tutorial may spotlight panels before the user completes prior steps.
    setActiveStep(step);
  }, []);

  const completeTutorial = useCallback(() => {
    setTutorialAnchor(null);
    setActiveStep('scores');
    closeGuide();
  }, [closeGuide]);

  useEffect(() => {
    programmeService.setCollection(FIXED_JUPAS_COLLECTION);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadLargestOfferRecommendations() {
      if (!selectedCollection) return;

      try {
        setRecommendationsLoading(true);
        setRecommendationsError('');
        let loadedProgrammes = allProgrammes;
        if (loadedProgrammes.length === 0) {
          const result = await programmeService.getAllProgrammes(selectedCollection, { summary: true });
          if (!result.success) {
            throw new Error(result.message || t('jupa.errorLoadingProgrammeRecommendations'));
          }
          loadedProgrammes = result.data || [];
          if (!cancelled) setAllProgrammes(loadedProgrammes);
        }

        const programmes = loadedProgrammes
          .filter((programme) => institutionMatches(programme, selectedInstitutions))
          .filter(programmeMatchesAdvancedFilters)
          .sort((a, b) => {
            const offerDiff = getLatestOfferTotal(b) - getLatestOfferTotal(a);
            return offerDiff || compareProgrammeCodes(a, b);
          });

        if (!cancelled) {
          setRecommendedProgrammes(programmes);
        }
      } catch (err) {
        if (!cancelled) {
          setRecommendationsError(err.message || t('jupa.errorLoadingProgrammeRecommendations'));
          setRecommendedProgrammes([]);
        }
      } finally {
        if (!cancelled) {
          setRecommendationsLoading(false);
        }
      }
    }

    loadLargestOfferRecommendations();
    return () => {
      cancelled = true;
    };
  }, [allProgrammes, selectedCollection, selectedInstitutions, programmeMatchesAdvancedFilters, t]);

  const finishUnavailableRefreshSession = useCallback((session) => {
    if (!session || unavailableRefreshSessionRef.current !== session) return;

    if (session.completionTimeoutId) {
      window.clearTimeout(session.completionTimeoutId);
    }

    const remaining = Math.max(0, 2000 - (Date.now() - session.startedAt));
    const complete = () => {
      if (unavailableRefreshSessionRef.current !== session) return;
      unavailableRefreshSessionRef.current = null;
      session.resolve();
    };

    if (remaining > 0) {
      session.completionTimeoutId = window.setTimeout(complete, remaining);
      return;
    }

    complete();
  }, []);

  const tryCompleteUnavailableRefresh = useCallback(() => {
    const session = unavailableRefreshSessionRef.current;
    if (!session || session.completionTimeoutId) return;

    if (session.requirementRequestId != null
      && requirementRefreshRequest?.id === session.requirementRequestId) {
      session.requirementStarted = true;
    }

    const classificationSettled = session.classificationCodes.size === 0
      || (
        ![...session.classificationCodes].some((code) => classificationLoadingCodes.has(code))
        && [...session.classificationCodes].every((code) => (
          Object.prototype.hasOwnProperty.call(classificationCacheRef.current.values, code)
          || classificationRetryRef.current.has(code)
        ))
      );

    const requirementSettled = session.requirementRequestId == null
      || (session.requirementStarted && requirementRefreshRequest == null);

    if (!classificationSettled || !requirementSettled) return;

    finishUnavailableRefreshSession(session);
  }, [
    classificationLoadingCodes,
    finishUnavailableRefreshSession,
    requirementRefreshRequest,
  ]);

  useEffect(() => {
    tryCompleteUnavailableRefresh();
  }, [
    classificationLoadingCodes,
    programmeClassificationMap,
    requirementRefreshRequest,
    tryCompleteUnavailableRefresh,
  ]);

  useEffect(() => () => {
    const session = unavailableRefreshSessionRef.current;
    if (session?.completionTimeoutId) {
      window.clearTimeout(session.completionTimeoutId);
    }
  }, []);

  useEffect(() => {
    async function loadProgrammeClassificationMap() {
      if (!selectedCollection || !isDseScoreSetComplete(committedScores)) {
        classificationCacheRef.current = { key: '', values: {}, pendingCodes: new Set() };
        setProgrammeClassificationMap({});
        setClassificationLoadingCodes(new Set());
        return;
      }

      const userScores = toScoresObject(committedScores);
      const cacheKey = JSON.stringify([selectedCollection, userScores]);
      if (classificationCacheRef.current.key !== cacheKey) {
        classificationCacheRef.current = { key: cacheKey, values: {}, pendingCodes: new Set() };
        classificationRetryRef.current = new Map();
        programmeEvaluationRequestIdRef.current += 1;
        setSelectedProgrammeEvaluation(null);
        setSelectedProgrammeEvaluationLoading(false);
        setClassificationLoadingCodes(new Set());
        // Keep the existing card layout/data mounted while its replacement is
        // calculated. Every classifier badge is masked by the loading spinner.
      }

      const programmesByCode = new Map();
      allProgrammes.forEach((programme) => {
        if (programme?.code) {
          programmesByCode.set(programme.code, programme);
        }
      });
      classificationRetryRef.current.forEach((programme, code) => {
        if (programme?.code && !programmesByCode.has(code)) {
          programmesByCode.set(code, programme);
        }
      });

      const programmesToClassify = Array.from(programmesByCode.values()).filter((programme) => (
        !Object.prototype.hasOwnProperty.call(classificationCacheRef.current.values, programme.code)
        && !classificationCacheRef.current.pendingCodes.has(programme.code)
      ));

      if (programmesToClassify.length === 0) {
        return;
      }

      const markClassificationPending = (programmes) => {
        programmes.forEach((programme) => {
          classificationCacheRef.current.pendingCodes.add(programme.code);
        });
        setClassificationLoadingCodes((current) => {
          const next = new Set(current);
          programmes.forEach((programme) => next.add(programme.code));
          return next;
        });
      };

      markClassificationPending(programmesToClassify);

      const year = getCollectionYear(selectedCollection);
      try {
        let newValues = {};

        const applyClassificationValues = (values) => {
          if (classificationCacheRef.current.key !== cacheKey) return;

          classificationCacheRef.current.values = {
            ...classificationCacheRef.current.values,
            ...values,
          };
          Object.keys(values).forEach((code) => {
            if (!isClassificationUnavailable(values[code]?.classification)) {
              classificationRetryRef.current.delete(code);
            }
          });
          setProgrammeClassificationMap({ ...classificationCacheRef.current.values });
        };

        const settleClassificationBatch = (batchProgrammes) => {
          if (classificationCacheRef.current.key !== cacheKey) return;
          batchProgrammes.forEach((programme) => {
            classificationCacheRef.current.pendingCodes.delete(programme.code);
          });
          setClassificationLoadingCodes((current) => {
            const next = new Set(current);
            batchProgrammes.forEach((programme) => next.delete(programme.code));
            return next;
          });
        };

        if (usesSplitJupasPipeline(selectedCollection)) {
          const fetchIndividualClassification = async (programme) => {
            try {
              const { response, data } = await fetchJsonWithRetry(`/api/jupas-2026/${year}/classify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  programmeCode: programme.code,
                  userScores,
                  skipBandCheck: true,
                })
              });
              const requirement = data?.requirement_fulfillment?.code;
              if (!response.ok
                || !data?.success
                || !Number.isInteger(data.classification)
                || !['P', 'C', 'R', 'W', 'U'].includes(requirement)) {
                return null;
              }
              return {
                classification: data.classification,
                requirement,
              };
            } catch (error) {
              return null;
            }
          };

          const fetchIndividualFallbacks = async (programmes) => {
            const outcomes = await mapWithConcurrency(
              programmes,
              3,
              async (programme) => [programme, await fetchIndividualClassification(programme)]
            );
            const values = {};
            const failed = [];
            outcomes.forEach(([programme, result]) => {
              if (result) values[programme.code] = result;
              else failed.push(programme);
            });
            return { values, failed };
          };

          const fetchSplitBatch = async (batchProgrammes, splitDepth = 0) => {
            if (batchProgrammes.length === 0) return { values: {}, failed: [] };
            const values = {};
            try {
              const { response, data } = await fetchJsonWithRetry(`/api/jupas-2026/${year}/classify-batch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  programmeCodes: batchProgrammes.map((programme) => programme.code),
                  userScores
                })
              });
              if (!response.ok || !data?.success || !data?.results_by_code) {
                throw new Error(data?.error_code || `classification_batch_http_${response.status}`);
              }

              const resultsByCode = data.results_by_code;
              const retryProgrammes = [];
              batchProgrammes.forEach((programme) => {
                const result = resultsByCode[programme.code];
                if (result?.cacheable !== false
                  && Number.isInteger(result?.classification)
                  && ['P', 'C', 'R', 'W', 'U'].includes(result?.requirement)) {
                  values[programme.code] = {
                    classification: result.classification,
                    requirement: result.requirement,
                  };
                } else {
                  retryProgrammes.push(programme);
                }
              });

              if (retryProgrammes.length === 0) return { values, failed: [] };
              if (retryProgrammes.length === 1 || splitDepth >= 2) {
                const recovered = await fetchIndividualFallbacks(retryProgrammes);
                Object.assign(values, recovered.values);
                return { values, failed: recovered.failed };
              }

              const midpoint = Math.ceil(retryProgrammes.length / 2);
              const recovered = await Promise.all([
                fetchSplitBatch(retryProgrammes.slice(0, midpoint), splitDepth + 1),
                fetchSplitBatch(retryProgrammes.slice(midpoint), splitDepth + 1),
              ]);
              recovered.forEach((result) => Object.assign(values, result.values));
              return {
                values,
                failed: recovered.flatMap((result) => result.failed),
              };
            } catch (err) {
              if (batchProgrammes.length === 1 || splitDepth >= 2) {
                return fetchIndividualFallbacks(batchProgrammes);
              }

              const midpoint = Math.ceil(batchProgrammes.length / 2);
              const recovered = await Promise.all([
                fetchSplitBatch(batchProgrammes.slice(0, midpoint), splitDepth + 1),
                fetchSplitBatch(batchProgrammes.slice(midpoint), splitDepth + 1),
              ]);
              recovered.forEach((result) => Object.assign(values, result.values));
              return {
                values,
                failed: recovered.flatMap((result) => result.failed),
              };
            }
          };

          const prioritySource = recommendedProgrammes.length > 0
            ? recommendedProgrammes
            : [...programmesToClassify].sort((a, b) => {
              const offerDiff = getLatestOfferTotal(b) - getLatestOfferTotal(a);
              return offerDiff || compareProgrammeCodes(a, b);
            });
          const priorityCodes = new Set(
            prioritySource
              .slice(0, CLASSIFICATION_PRIORITY_SIZE)
              .map((programme) => programme.code)
          );
          const priorityProgrammes = programmesToClassify.filter((programme) => priorityCodes.has(programme.code));
          const backgroundProgrammes = programmesToClassify.filter((programme) => !priorityCodes.has(programme.code));
          const backgroundBatches = [];
          for (let start = 0; start < backgroundProgrammes.length; start += CLASSIFICATION_BACKGROUND_BATCH_SIZE) {
            backgroundBatches.push(backgroundProgrammes.slice(start, start + CLASSIFICATION_BACKGROUND_BATCH_SIZE));
          }

          const processBatch = async (batchProgrammes) => {
            if (batchProgrammes.length === 0) return;
            try {
              const { values, failed } = await fetchSplitBatch(batchProgrammes);
              if (classificationCacheRef.current.key !== cacheKey) return;
              applyClassificationValues(values);
              failed.forEach((programme) => {
                classificationRetryRef.current.set(programme.code, programme);
              });
            } finally {
              settleClassificationBatch(batchProgrammes);
            }
          };
          const processBackground = async () => {
            for (const batch of backgroundBatches) {
              await processBatch(batch);
            }
          };

          await Promise.all([
            processBatch(priorityProgrammes),
            processBackground(),
          ]);

          for (let round = 0; round < CLASSIFICATION_AUTO_RETRY_ROUNDS; round += 1) {
            if (classificationCacheRef.current.key !== cacheKey) break;

            const failedProgrammes = programmesToClassify.filter((programme) => {
              const entry = classificationCacheRef.current.values[programme.code];
              return classificationRetryRef.current.has(programme.code)
                || isClassificationUnavailable(entry?.classification)
                || isRequirementUnavailable(entry?.requirement);
            });
            if (failedProgrammes.length === 0) break;

            markClassificationPending(failedProgrammes);
            for (let start = 0; start < failedProgrammes.length; start += CLASSIFICATION_BACKGROUND_BATCH_SIZE) {
              if (classificationCacheRef.current.key !== cacheKey) break;
              await processBatch(
                failedProgrammes.slice(start, start + CLASSIFICATION_BACKGROUND_BATCH_SIZE)
              );
            }
          }
        } else {
          const classifyUrl = `/api/programmes/${encodeURIComponent(selectedCollection)}/classify`;
          const classifications = await mapWithConcurrency(
            programmesToClassify,
            3,
            async (programme) => {
              try {
                const classificationRequest = await fetchJsonWithRetry(classifyUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ programmeCode: programme.code, userScores })
                });
                const { response: classificationResponse, data } = classificationRequest;
                if (!classificationResponse.ok || !data?.success || data?.classification === -1) {
                  return [programme.code, {
                    classification: -1,
                    requirement: 'U',
                  }];
                }
                return [
                  programme.code,
                  {
                    classification: data?.classification,
                    requirement: data?.requirement_fulfillment?.code || 'U',
                  }
                ];
              } catch (err) {
                return [programme.code, {
                  classification: -1,
                  requirement: 'U',
                }];
              }
            }
          );
          newValues = Object.fromEntries(classifications.filter(Boolean));
        }

        if (Object.keys(newValues).length > 0) {
          applyClassificationValues(newValues);
        }
      } finally {
        if (classificationCacheRef.current.key === cacheKey) {
          programmesToClassify.forEach((programme) => {
            classificationCacheRef.current.pendingCodes.delete(programme.code);
          });
        }
        if (classificationCacheRef.current.key === cacheKey) {
          setClassificationLoadingCodes((current) => {
            const next = new Set(current);
            programmesToClassify.forEach((programme) => next.delete(programme.code));
            return next;
          });
        }
      }
    }

    loadProgrammeClassificationMap();
  }, [selectedCollection, committedScores, allProgrammes, recommendedProgrammes, classificationRefreshNonce]);

  useEffect(() => {
    if (!requirementRefreshRequest) return;

    if (!selectedCollection
      || !isDseScoreSetComplete(committedScores)
      || !supportsRequirementFulfillment(selectedCollection)) {
      return;
    }

    const requestId = requirementRequestIdRef.current + 1;
    requirementRequestIdRef.current = requestId;
    const userScores = toScoresObject(committedScores);
    const cacheKey = JSON.stringify([selectedCollection, userScores]);
    const year = getCollectionYear(selectedCollection);
    const fulfillmentUrl = `/api/jupas-2026/${year}/requirement-fulfillment-batch`;
    const selectedRanks = new Map(
      requirementRefreshRequest.orderedProgrammes
        .map((programme, index) => [programme.code, index + 1])
    );
    const programmesByCode = new Map();
    requirementRefreshRequest.programmes.forEach((programme) => {
      if (programme?.code) programmesByCode.set(programme.code, programme);
    });
    const programmes = Array.from(programmesByCode.values());
    if (programmes.length === 0) return;

    async function refreshRequirementFulfillment() {
      try {
        const requestEntries = programmes.map((programme) => {
          const selectedRank = selectedRanks.get(programme.code);
          const hasJupasBand = shouldCheckJupasBandRequirement(activeStep, selectedRank);
          return hasJupasBand
            ? { programmeCode: programme.code, ranking: selectedRank, skipBandCheck: false }
            : { programmeCode: programme.code, skipBandCheck: true };
        });
        const outcomesByCode = new Map();
        let fallbackOutcome = null;

        for (let start = 0; start < requestEntries.length; start += REQUIREMENT_BATCH_SIZE) {
          const batch = requestEntries.slice(start, start + REQUIREMENT_BATCH_SIZE);
          const { response, data } = await fetchJsonWithRetry(fulfillmentUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userScores, programmes: batch })
          });

          if (!response.ok || !data?.success || !data?.results_by_code) {
            fallbackOutcome = { response, data };
            break;
          }

          batch.forEach(({ programmeCode }) => {
            outcomesByCode.set(programmeCode, {
              response,
              data,
              result: data.results_by_code[programmeCode],
            });
          });
        }

        if (requirementRequestIdRef.current !== requestId
          || classificationCacheRef.current.key !== cacheKey) return;

        programmes.forEach((programme) => {
          const outcome = outcomesByCode.get(programme.code) || fallbackOutcome;
          const response = outcome?.response;
          const result = outcome?.result;
          const requirement = response?.ok
            && result?.success
            && ['P', 'C', 'R', 'W', 'U'].includes(result.code)
            ? result.code
            : 'U';

          const selectedRank = selectedRanks.get(programme.code);
          const hasJupasBand = shouldCheckJupasBandRequirement(activeStep, selectedRank);
          const current = classificationCacheRef.current.values[programme.code];
          classificationCacheRef.current.values[programme.code] = {
            ...current,
            classification: Number.isInteger(current?.classification) ? current.classification : -1,
            ...(hasJupasBand
              ? { rankedRequirement: requirement }
              : { requirement }),
          };
        });
        setProgrammeClassificationMap({ ...classificationCacheRef.current.values });
      } catch (err) {
        if (requirementRequestIdRef.current !== requestId
          || classificationCacheRef.current.key !== cacheKey) return;

        programmes.forEach((programme) => {
          const selectedRank = selectedRanks.get(programme.code);
          const hasJupasBand = shouldCheckJupasBandRequirement(activeStep, selectedRank);
          const current = classificationCacheRef.current.values[programme.code];
          classificationCacheRef.current.values[programme.code] = {
            ...current,
            classification: Number.isInteger(current?.classification) ? current.classification : -1,
            ...(hasJupasBand
              ? { rankedRequirement: 'U' }
              : { requirement: 'U' }),
          };
        });
        setProgrammeClassificationMap({ ...classificationCacheRef.current.values });
      }
    }

    refreshRequirementFulfillment().finally(() => {
      if (requirementRequestIdRef.current === requestId) {
        setRequirementRefreshRequest((current) => (
          current?.id === requirementRefreshRequest.id ? null : current
        ));
      }
    });
  }, [activeStep, requirementRefreshRequest, selectedCollection, committedScores]);

  const convertSavedOrderToProgrammes = useCallback(async (savedOrder) => {
    const orderedCodes = Object.entries(savedOrder || {})
      .sort(([, posA], [, posB]) => posA - posB)
      .map(([code]) => code);

    if (!orderedCodes.length || !selectedCollection) {
      return [];
    }

    const response = await fetch(`/api/programmes/${encodeURIComponent(selectedCollection)}/codes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ codes: orderedCodes })
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.message || t('jupa.failedToFetchProgrammeInformation'));
    }

    return orderedCodes
      .map((code) => result.programmes_by_code?.[code])
      .filter(Boolean);
  }, [selectedCollection, t]);

  useEffect(() => {
    let cancelled = false;

    async function loadSavedProgrammeOrder() {
      if (!isAuthenticated || !selectedCollection) return;
      if (guestResumeState?.selectedProgrammes?.length) return;

      try {
        setOrderSaving(true);
        const token = localStorage.getItem('token');
        const response = await fetch('/api/jupas-order/load', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || t('jupa.failedToLoadSavedProgrammeOrder'));
        }

        const programmes = await convertSavedOrderToProgrammes(data.programmeOrder);
        if (!cancelled && programmes.length) {
          setSelectedProgrammes(programmes);
        }
      } catch (err) {
        if (!cancelled) {
          setOrderSaveStatus(t('jupa.failedToLoadSavedProgrammeOrder'));
        }
      } finally {
        if (!cancelled) {
          setOrderSaving(false);
        }
      }
    }

    loadSavedProgrammeOrder();
    return () => {
      cancelled = true;
    };
  }, [convertSavedOrderToProgrammes, guestResumeState, isAuthenticated, selectedCollection, t]);

  const saveProgrammeOrder = async (programmesToSave) => {
    if (!isAuthenticated) return false;

    try {
      setOrderSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/jupas-order/save', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ programmeOrder: toSavedOrder(programmesToSave) })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || t('jupa.failedToSaveProgrammeOrder'));
      }

      setOrderSaveStatus(t('jupa.jupasOrderSaved'));
      setTimeout(() => setOrderSaveStatus(''), 3000);
      refreshUserProfile().catch(() => {});
      return true;
    } catch (err) {
      setOrderSaveStatus(t('jupa.failedToSaveJupasOrder'));
      return false;
    } finally {
      setOrderSaving(false);
    }
  };
  saveProgrammeOrderRef.current = saveProgrammeOrder;

  useEffect(() => {
    if (!isAuthenticated || !guestResumeState || guestResumeAppliedRef.current) return;
    guestResumeAppliedRef.current = true;

    const restoredProgrammes = Array.isArray(guestResumeState.selectedProgrammes)
      ? guestResumeState.selectedProgrammes
      : [];
    if (restoredProgrammes.length > 0) {
      setSelectedProgrammes(restoredProgrammes);
      saveProgrammeOrderRef.current?.(restoredProgrammes).then((saved) => {
        if (saved) markGuestResumePersisted('programmes');
      });
    }
    if (Array.isArray(guestResumeState.scores)) {
      setCommittedScores(guestResumeState.scores);
    }
    if (restoredProgrammes.length === 0) markGuestResumePersisted('programmes');
  }, [guestResumeState, isAuthenticated, markGuestResumePersisted]);

  const openProgrammeDetails = (programme, orderedProgrammes = selectedProgrammes) => {
    const requestId = programmeEvaluationRequestIdRef.current + 1;
    programmeEvaluationRequestIdRef.current = requestId;
    setSelectedProgramme(programme);
    setSelectedProgrammeEvaluation(null);
    setSelectedProgrammeEvaluationLoading(false);

    const cachedDetail = programmeDetailCacheRef.current.get(programme.code);
    if (cachedDetail) {
      setSelectedProgramme((current) => (
        current?.code === programme.code ? { ...programme, ...cachedDetail } : current
      ));
    } else {
      programmeService.getProgrammeByCode(programme.code, selectedCollection)
        .then((result) => {
          if (!result?.success || !result.data) return;
          programmeDetailCacheRef.current.set(programme.code, result.data);
          setSelectedProgramme((current) => (
            current?.code === programme.code ? { ...programme, ...result.data } : current
          ));
        })
        .catch(() => {});
    }

    if (!isDseScoreSetComplete(committedScores)) return;

    setSelectedProgrammeEvaluationLoading(true);
    const rankIndex = orderedProgrammes.findIndex((entry) => entry.code === programme.code);
    const ranking = rankIndex >= 0 ? rankIndex + 1 : null;
    const year = getCollectionYear(selectedCollection);
    const shouldCheckBand = shouldCheckJupasBandRequirement(activeStep, ranking);

    fetchJsonWithRetry(`/api/jupas-2026/${year}/classify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        programmeCode: programme.code,
        userScores: toScoresObject(committedScores),
        ...(shouldCheckBand ? { ranking, skipBandCheck: false } : { skipBandCheck: true }),
      })
    }).then(({ response, data }) => {
      if (programmeEvaluationRequestIdRef.current !== requestId) return;
      if (!response.ok || !data?.success) {
        setSelectedProgrammeEvaluation({
          classification: -1,
          requirement_fulfillment: {
            classification: 'U',
            code: 'U',
            reason_code: 'detail_evaluation_unavailable',
            message: data?.message || 'Programme evaluation is temporarily unavailable.',
          },
          classification_error: {
            code: 'detail_evaluation_unavailable',
            message: data?.message || 'Programme evaluation is temporarily unavailable.',
          },
        });
        return;
      }
      setSelectedProgrammeEvaluation({
        ...data,
        requirement_check: data.pipeline?.checkRequirement,
        calculation_result: data.pipeline?.calculateScore,
      });
    }).catch((error) => {
      if (programmeEvaluationRequestIdRef.current !== requestId) return;
      setSelectedProgrammeEvaluation({
        classification: -1,
        requirement_fulfillment: {
          classification: 'U',
          code: 'U',
          reason_code: 'detail_evaluation_unavailable',
          message: error?.message || 'Programme evaluation is temporarily unavailable.',
        },
        classification_error: {
          code: 'detail_evaluation_unavailable',
          message: error?.message || 'Programme evaluation is temporarily unavailable.',
        },
      });
    }).finally(() => {
      if (programmeEvaluationRequestIdRef.current === requestId) {
        setSelectedProgrammeEvaluationLoading(false);
      }
    });
  };

  const handleProgrammeSelect = (programme) => {
    if (selectedProgrammes.some((selected) => selected.code === programme.code)) {
      openProgrammeDetails(programme);
      return;
    }

    if (!isAuthenticated && selectedProgrammes.length >= 3) {
      setGuestStoragePrompt({ reason: 'limit', onProceed: () => {} });
      return;
    }

    const nextProgrammes = [...selectedProgrammes, programme];
    setSelectedProgrammes(nextProgrammes);
    openProgrammeDetails(programme, nextProgrammes);
    saveProgrammeOrder(nextProgrammes);
  };

  const removeProgramme = (programme) => {
    const nextProgrammes = selectedProgrammes.filter((selected) => selected.code !== programme.code);
    setSelectedProgrammes(nextProgrammes);
    if (selectedProgramme?.code === programme.code) {
      setSelectedProgramme(null);
      setSelectedProgrammeEvaluation(null);
      programmeEvaluationRequestIdRef.current += 1;
      setSelectedProgrammeEvaluationLoading(false);
    }
    saveProgrammeOrder(nextProgrammes);
  };

  const handleRemoveProgramme = (programme) => {
    if (!isAuthenticated) {
      setGuestStoragePrompt({
        reason: 'remove',
        onProceed: () => removeProgramme(programme),
      });
      return;
    }
    removeProgramme(programme);
  };

  const cacheGuestCalculatorAndNavigate = (path) => {
    cacheJupasGuestState({ scores, selectedProgrammes, activeStep });
    localStorage.setItem(JUPAS_GUEST_NOTICE_SEEN_KEY, '1');
    navigate(`${path}?returnTo=${encodeURIComponent('/jupas-calculator')}`);
  };

  const startPointerDrag = (event, session) => {
    const source = event.currentTarget;
    const { index, rect } = session;
    dragPreviewRef.current?.element.remove();
    const preview = source.cloneNode(true);
    preview.removeAttribute('draggable');
    preview.setAttribute('aria-hidden', 'true');
    preview.classList.remove('opacity-0');
    preview.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    Object.assign(preview.style, {
      position: 'fixed',
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      margin: '0',
      opacity: '0.96',
      pointerEvents: 'none',
      transform: 'scale(0.99)',
      transition: 'none',
      zIndex: '10000',
      boxShadow: '0 14px 32px rgba(15, 23, 42, 0.18)'
    });
    document.body.appendChild(preview);
    dragPreviewRef.current = {
      element: preview,
      left: rect.left,
      pointerOffsetY: session.pointerOffsetY
    };
    session.active = true;
    session.previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    setDraggedIndex(index);
    setDragOverIndex(index);
    setDraggedCardHeight(rect.height);
  };

  const handleOrderPointerDown = (event, index) => {
    if (event.button !== 0 || event.target.closest('button')) return;
    const source = event.currentTarget;
    const rect = source.getBoundingClientRect();
    source.setPointerCapture(event.pointerId);
    dragSessionRef.current = {
      active: false,
      index,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      pointerOffsetY: event.clientY - rect.top,
      rect,
      cardCenters: Array.from(orderListRef.current?.children || []).map((card, cardIndex) => {
        const cardRect = card.getBoundingClientRect();
        return { index: cardIndex, centerY: cardRect.top + cardRect.height / 2 };
      })
    };
  };

  const handleOrderPointerMove = (event) => {
    const session = dragSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    if (!session.active) {
      const distance = Math.hypot(event.clientX - session.startX, event.clientY - session.startY);
      if (distance < 5) return;
      startPointerDrag(event, session);
    }

    event.preventDefault();
    const previewState = dragPreviewRef.current;
    if (previewState) {
      previewState.element.style.left = `${previewState.left}px`;
      previewState.element.style.top = `${event.clientY - previewState.pointerOffsetY}px`;
    }
    const closestCard = session.cardCenters.reduce((closest, card) => (
      Math.abs(card.centerY - event.clientY) < Math.abs(closest.centerY - event.clientY)
        ? card
        : closest
    ));
    session.overIndex = closestCard.index;
    setDragOverIndex(closestCard.index);
  };

  const clearDragState = () => {
    dragPreviewRef.current?.element.remove();
    dragPreviewRef.current = null;
    const session = dragSessionRef.current;
    if (session?.previousUserSelect !== undefined) {
      document.body.style.userSelect = session.previousUserSelect;
    }
    dragSessionRef.current = null;
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDraggedCardHeight(0);
  };

  const handleOrderPointerUp = (event) => {
    const session = dragSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!session.active) {
      const programme = selectedProgrammes[session.index];
      clearDragState();
      if (programme) {
        openProgrammeDetails(programme);
      }
      return;
    }

    const dropIndex = session.overIndex ?? session.index;
    if (session.index === dropIndex) {
      clearDragState();
      return;
    }
    const nextProgrammes = [...selectedProgrammes];
    const [draggedProgramme] = nextProgrammes.splice(session.index, 1);
    nextProgrammes.splice(dropIndex, 0, draggedProgramme);
    setSelectedProgrammes(nextProgrammes);
    const affectedStart = Math.min(session.index, dropIndex);
    const affectedEnd = Math.max(session.index, dropIndex);
    setRequirementRefreshRequest({
      id: Date.now(),
      programmes: nextProgrammes.slice(affectedStart, affectedEnd + 1),
      orderedProgrammes: nextProgrammes,
    });
    clearDragState();
    saveProgrammeOrder(nextProgrammes);
  };

  const handleOrderPointerCancel = () => {
    clearDragState();
  };

  useEffect(() => () => {
    dragPreviewRef.current?.element.remove();
    if (dragSessionRef.current?.previousUserSelect !== undefined) {
      document.body.style.userSelect = dragSessionRef.current.previousUserSelect;
    }
  }, []);

  const renderProgrammeDetailsPanel = () => (
    <ProgrammeDetailsPanel
      programme={selectedProgramme}
      selectedCollection={selectedCollection}
      classificationData={selectedProgrammeEvaluation}
      classificationLoading={selectedProgrammeEvaluationLoading}
    />
  );

  const refreshUnavailableEvaluations = useCallback(() => {
    const existingSession = unavailableRefreshSessionRef.current;
    if (existingSession?.promise) {
      return existingSession.promise;
    }

    const promise = new Promise((resolve) => {
      const startedAt = Date.now();
      const finishImmediately = () => {
        const remaining = Math.max(0, 2000 - (Date.now() - startedAt));
        window.setTimeout(resolve, remaining);
      };

      if (!selectedCollection || !isDseScoreSetComplete(committedScores)) {
        finishImmediately();
        return;
      }

      const visibleProgrammes = [
        ...recommendedProgrammes,
        ...(programmeSearchState.query.trim() ? programmeSearchState.results : []),
        ...selectedProgrammes,
      ];
      const uniqueByCode = new Map();
      visibleProgrammes.forEach((programme) => {
        if (programme?.code) uniqueByCode.set(programme.code, programme);
      });

      const programmesToReclassify = [];
      const programmesToRefreshRequirement = [];

      uniqueByCode.forEach((programme, code) => {
        const entry = classificationCacheRef.current.values[code];
        if (isClassificationUnavailable(entry?.classification)
          || classificationRetryRef.current.has(code)) {
          delete classificationCacheRef.current.values[code];
          classificationCacheRef.current.pendingCodes.delete(code);
          programmesToReclassify.push(programme);
        }
        if (supportsRequirementFulfillment(selectedCollection)) {
          if (isRequirementUnavailable(entry?.requirement)) {
            programmesToRefreshRequirement.push(programme);
          }
        }
      });

      if (programmesToReclassify.length === 0 && programmesToRefreshRequirement.length === 0) {
        finishImmediately();
        return;
      }

      const session = {
        resolve,
        startedAt,
        promise: null,
        classificationCodes: new Set(programmesToReclassify.map((programme) => programme.code)),
        requirementRequestId: null,
        requirementDispatched: false,
        requirementStarted: false,
        completionTimeoutId: null,
      };
      unavailableRefreshSessionRef.current = session;
      session.promise = promise;

      if (programmesToReclassify.length > 0) {
        programmesToReclassify.forEach((programme) => {
          classificationRetryRef.current.set(programme.code, programme);
        });
        setProgrammeClassificationMap({ ...classificationCacheRef.current.values });
        setClassificationRefreshNonce((current) => current + 1);
      }

      if (programmesToRefreshRequirement.length > 0) {
        const requestId = Date.now();
        session.requirementRequestId = requestId;
        session.requirementDispatched = true;
        setRequirementRefreshRequest({
          id: requestId,
          programmes: programmesToRefreshRequirement,
          orderedProgrammes: selectedProgrammes,
        });
      }
    });

    return promise;
  }, [
    committedScores,
    programmeSearchState.query,
    programmeSearchState.results,
    recommendedProgrammes,
    selectedCollection,
    selectedProgrammes,
  ]);

  const programmeSearchCommonProps = {
    selectedCollection,
    onProgrammeSelect: handleProgrammeSelect,
    selectedProgrammesCount: selectedProgrammes.length,
    selectedProgrammeCodes: selectedProgrammes.map((programme) => programme.code),
    selectedInstitutions,
    onInstitutionsChange: setSelectedInstitutions,
    selectedRequirementFilters,
    onRequirementFiltersChange: setSelectedRequirementFilters,
    selectedClassificationFilters,
    onClassificationFiltersChange: setSelectedClassificationFilters,
    filterProgramme: programmeMatchesAdvancedFilters,
    onRefreshUnavailable: refreshUnavailableEvaluations,
  };

  const programmeListPlaceholderClass = `flex items-center justify-center rounded-lg border border-edge bg-surface px-4 text-center ${JUPAS_SIX_ROW_LIST_MIN_HEIGHT_CLASS}`;

  const renderProgrammesListLoadingPlaceholder = (messageKey) => (
    <div className={programmeListPlaceholderClass} role="status">
      <LoadingSpinner
        size="medium"
        text={t(messageKey)}
        showText
        textClassName="text-sm text-ink-subtle"
      />
    </div>
  );

  const renderProgrammeListContent = () => {
    if (programmesListSpinnerVisible) {
      const messageKey = isProgrammeSearchActive
        ? 'common.loading'
        : 'jupa.loadingProgrammeRecommendations';
      return renderProgrammesListLoadingPlaceholder(messageKey);
    }

    if (isProgrammeSearchActive) {
      if (programmeSearchState.error) {
        return (
          <div className={programmeListPlaceholderClass}>
            <p className="text-sm text-red-600">{programmeSearchState.error}</p>
          </div>
        );
      }
      if (programmeSearchState.results.length === 0) {
        return (
          <div className={`${programmeListPlaceholderClass} text-sm text-ink-subtle`}>
            {t('jupa.noProgrammesFoundMatching')} &quot;{programmeSearchState.query}&quot;
          </div>
        );
      }
      return (
        <ProgrammeRecommendations
          recommendedProgrammes={programmeSearchState.results}
          selectedProgrammes={selectedProgrammes}
          selectedProgrammeCode={selectedProgramme?.code}
          onAddProgramme={handleProgrammeSelect}
          onRemoveProgramme={handleRemoveProgramme}
          onSelectProgramme={openProgrammeDetails}
          classificationByCode={programmeClassificationMap}
          classificationLoadingCodes={classificationLoadingCodes}
          layout="compact-list"
        />
      );
    }

    if (recommendationsError) {
      return (
        <div className={programmeListPlaceholderClass}>
          <p className="text-sm text-red-600">{recommendationsError}</p>
        </div>
      );
    }
    return (
      <ProgrammeRecommendations
        recommendedProgrammes={recommendedProgrammes}
        selectedProgrammes={selectedProgrammes}
        selectedProgrammeCode={selectedProgramme?.code}
        onAddProgramme={handleProgrammeSelect}
        onRemoveProgramme={handleRemoveProgramme}
        onSelectProgramme={openProgrammeDetails}
        classificationByCode={programmeClassificationMap}
        classificationLoadingCodes={classificationLoadingCodes}
        layout="compact-list"
      />
    );
  };

  const renderTutorialProgrammeList = () => (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-subtle">
        {t('pageTutorial.jupas.demoDataLabel')}
      </p>
      <ProgrammeRecommendations
        recommendedProgrammes={TUTORIAL_PROGRAMMES}
        selectedProgrammes={[]}
        onAddProgramme={() => {}}
        onSelectProgramme={() => {}}
        classificationByCode={TUTORIAL_CLASSIFICATIONS}
        layout="compact-list"
      />
    </div>
  );

  const renderOrderSection = () => {
    return (
      <section>
        {selectedProgrammes.length === 0 ? (
          <p className="mb-3 text-xs text-ink-subtle">{t('jupa.emptyOrderHint')}</p>
        ) : null}

        <div ref={orderListRef} className="space-y-2">
          {selectedProgrammes.map((programme, index) => {
            const shouldShiftUp = draggedIndex !== null
              && dragOverIndex !== null
              && draggedIndex < dragOverIndex
              && index > draggedIndex
              && index <= dragOverIndex;
            const shouldShiftDown = draggedIndex !== null
              && dragOverIndex !== null
              && draggedIndex > dragOverIndex
              && index >= dragOverIndex
              && index < draggedIndex;
            const dragShiftY = shouldShiftUp
              ? -(draggedCardHeight + 8)
              : shouldShiftDown
                ? draggedCardHeight + 8
                : 0;

            return (
              <JupasProgrammeCard
                key={programme.code}
                programme={programme}
                rank={getJupasOrderRankLabel(index)}
                bandColor={getJupasOrderBandColor(index)}
                preferShortName={i18n.language?.startsWith('en')}
                classificationData={rankedProgrammeClassificationMap[programme.code]}
                classificationLoading={classificationLoadingCodes.has(programme.code)}
                statsYear={selectedYear}
                showInlineDetails={false}
                isSelected={selectedProgramme?.code === programme.code}
                onToggle={() => openProgrammeDetails(programme)}
                onRemove={handleRemoveProgramme}
                showDragHandle
                onPointerDown={(event) => handleOrderPointerDown(event, index)}
                onPointerMove={handleOrderPointerMove}
                onPointerUp={handleOrderPointerUp}
                onPointerCancel={handleOrderPointerCancel}
                isDragging={draggedIndex === index}
                dragShiftY={dragShiftY}
              />
            );
          })}
        </div>
      </section>
    );
  };

  const renderTutorialOrderSection = () => (
    <section>
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-subtle">
        {t('pageTutorial.jupas.demoDataLabel')}
      </p>
      <div className="space-y-2">
        {TUTORIAL_PROGRAMMES.map((programme, index) => (
          <JupasProgrammeCard
            key={programme.code}
            programme={programme}
            rank={getJupasOrderRankLabel(index)}
            bandColor={getJupasOrderBandColor(index)}
            preferShortName={i18n.language?.startsWith('en')}
            classificationData={TUTORIAL_CLASSIFICATIONS[programme.code]}
            showInlineDetails={false}
            onToggle={() => {}}
            showDragHandle
          />
        ))}
      </div>
    </section>
  );

  return (
    <div className="jupas-calculator-page mx-auto w-full max-w-7xl min-w-0 overflow-x-clip px-4 py-8">
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="hidden min-w-0 text-center md:block md:text-left">
            <h1 className="text-3xl font-bold tracking-normal text-ink">{t('navigation.jupaSelect')}</h1>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-center gap-2 md:justify-end">
            <TutorialInlineButton onClick={openGuide} />
            <BugReportHelperButton page="jupas-calculator" />
          </div>
        </div>
      </div>

      <JupasCalculatorWorkspace
        activeStep={activeStep}
        onStepChange={handleStepChange}
        scoresComplete={scoresComplete}
        scoresHeaderAccessory={(
          <StorageStatusIndicator
            isAuthenticated={isAuthenticated}
            isSaving={scoreSaveState.isLoading}
            hasError={scoreSaveState.saveStatus === t('dse.failedToSaveScores')
              || scoreSaveState.saveStatus === t('dse.failedToLoadSavedScores')}
          />
        )}
        programmesComplete={programmesComplete}
        programmesHeaderAccessory={(
          <StorageStatusIndicator
            isAuthenticated={isAuthenticated}
            isSaving={orderSaving}
            hasError={orderSaveStatus === t('jupa.failedToSaveJupasOrder')
              || orderSaveStatus === t('jupa.failedToLoadSavedProgrammeOrder')}
          />
        )}
        rankingHeaderAccessory={(
          <StorageStatusIndicator
            isAuthenticated={isAuthenticated}
            isSaving={orderSaving}
            hasError={orderSaveStatus === t('jupa.failedToSaveJupasOrder')
              || orderSaveStatus === t('jupa.failedToLoadSavedProgrammeOrder')}
          />
        )}
        scoresRef={dseTutorialRef}
        programmesRef={searchTutorialRef}
        rankingRef={orderingTutorialRef}
        scoresSidebar={<JupasScoreSidebar scores={scores} />}
        programmesSidebar={(
          <JupasSelectorSidebar selectedProgrammes={selectedProgrammes} />
        )}
        rankingSidebar={<JupasRankingSidebar programmes={selectedProgrammes} />}
        scoresContent={(
          <div className="grid items-stretch gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="min-w-0 text-left">
              <DSEScoreInputer
                className="!mx-0"
                seedScores={seedScoresFromPromo}
                persistSeedScores={Boolean(isAuthenticated && guestResumeState?.scores?.length)}
                onSeedScoresPersisted={() => markGuestResumePersisted('scores')}
                onScoresChange={setScores}
                onSaveStatusChange={setScoreSaveState}
              />
            </div>
            <div className="flex min-h-[20rem] h-full w-full flex-col items-center justify-center rounded-2xl bg-surface-muted px-8 py-8 text-center text-ink">
              <div className="grid w-full grid-cols-2 gap-4">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-ink-subtle">{t('dse.scoreSummaryBest5')}</span>
                  <strong className="mt-2 block text-5xl font-bold leading-none tracking-tight text-ink">{scoreSummary.best5Score}</strong>
                </div>
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-ink-subtle">{t('dse.scoreSummaryTotal')}</span>
                  <strong className="mt-2 block text-5xl font-bold leading-none tracking-tight text-ink">{scoreSummary.totalScore}</strong>
                </div>
              </div>
              <ul className="mt-8 w-full max-w-[16rem] space-y-2 text-center text-sm font-medium leading-6">
                <li className="flex items-center justify-center gap-2.5">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                  <span>{t('jupa.workspace.scoreContinuePointComplete')}</span>
                </li>
                <li className="flex items-center justify-center gap-2.5">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                  <span>
                    {t('jupa.workspace.scoreContinuePointNext', {
                      step: t('jupa.workspace.stepProgrammes'),
                    })}
                  </span>
                </li>
              </ul>
              <span className="mt-3 text-6xl font-light leading-none text-ink-muted" aria-hidden>→</span>
            </div>
          </div>
        )}
        programmesContent={(
          <div className="flex min-h-0 flex-col gap-4">
            <ProgrammeSearch
              {...programmeSearchCommonProps}
              includeSearch={false}
            />

            <ProgrammeSearch
              {...programmeSearchCommonProps}
              includeFilters={false}
              inlineResults
              onSearchStateChange={setProgrammeSearchState}
            />

            <JupasProgrammeSplitLayout
              list={guideOpen && tutorialAnchor === 'search'
                ? renderTutorialProgrammeList()
                : renderProgrammeListContent()}
              detail={renderProgrammeDetailsPanel()}
            />
          </div>
        )}
        rankingContent={(
          <JupasProgrammeSplitLayout
            list={guideOpen && tutorialAnchor === 'ordering'
              ? renderTutorialOrderSection()
              : renderOrderSection()}
            detail={renderProgrammeDetailsPanel()}
          />
        )}
      />

      <div className="mt-8">
        <ContactBox />
      </div>

      <PageTutorialGuide
        active={guideOpen}
        onClose={closeGuide}
        onComplete={completeTutorial}
        t={t}
        steps={jupasGuideSteps}
        anchorRefs={tutorialAnchorRefs}
        onAnchorChange={handleTutorialAnchorChange}
      />

      <GuestJupasStorageModal
        open={Boolean(guestStoragePrompt)}
        reason={guestStoragePrompt?.reason}
        onProceed={() => {
          const pendingAction = guestStoragePrompt?.onProceed;
          setGuestStoragePrompt(null);
          pendingAction?.();
        }}
        onRegister={() => cacheGuestCalculatorAndNavigate('/register')}
        onLogin={() => cacheGuestCalculatorAndNavigate('/login')}
      />
    </div>
  );
};

export default JUPASCalculator;
